import columnsMetadata from "@utils/helpers/allColumnsMetadata.json";
import { DataAuditRecord } from "@/types";

/**
 * Derives a human-readable field-level diff from a `data_audit` row.
 *
 * The `data_audit_trigger_fn` triggers (migration `000041`) store the whole row
 * on every write, shaped differently per action:
 *
 *   UPDATE / SOFT_DELETE → `{ old: {...}, new: {...} }`
 *   INSERT  / DELETE     → the bare row, with no old/new envelope
 *
 * Only the first shape can be diffed; the others carry no counterpart to
 * compare against and are reported as `null`.
 */

// ─── Noise suppression ───────────────────────────────────────────────────────

/**
 * Columns excluded from every diff. These move on *every* write — a full-record
 * PUT rewrites all columns, so `modified_datetime` differs even when the user
 * changed nothing — and reporting them would bury the fields that did change.
 *
 * Note this is why an UPDATE can legitimately diff to zero changes; callers
 * must render that case rather than assume at least one entry.
 */
const NOISE_COLUMNS = new Set(["modified_by", "modified_datetime"]);

/** Nested paths below this many segments are collapsed into their parent. */
const MAX_LEAF_DEPTH = 6;

/** Cap on leaves reported per blob column; the remainder is counted, not listed. */
export const MAX_LEAVES_PER_BLOB = 12;

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single changed leaf inside a JSONB blob column. */
export interface AuditLeafChange {
  /** Display path within the blob, e.g. `claims › calculated › total_rx`. */
  path: string;
  before: unknown;
  after: unknown;
}

/** A single changed column on the audited row. */
export interface AuditFieldChange {
  /** Raw column name, e.g. `member_id`. */
  key: string;
  /** Human label from `allColumnsMetadata.json`, else humanised from `key`. */
  label: string;
  /**
   * `blob` when the value is a JSON object/array on either side. Blobs are
   * summarised rather than printed — see `leaves`.
   */
  kind: "scalar" | "blob";
  before: unknown;
  after: unknown;
  /** Changed leaves, for `blob` fields only. Capped at {@link MAX_LEAVES_PER_BLOB}. */
  leaves?: AuditLeafChange[];
  /** Leaves beyond the cap, for a "+N more" affordance. */
  hiddenLeafCount?: number;
}

// ─── Value comparison ────────────────────────────────────────────────────────

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** `null` and a missing key are the same absence as far as a reader is concerned. */
const isAbsent = (v: unknown): boolean => v === null || v === undefined;

/**
 * Structural equality with two deliberate tolerances:
 *
 *  - `null` and `undefined` (absent key) compare equal. JSONB blobs gain and
 *    lose keys as the writer's struct evolves, and "this key did not exist"
 *    is not a change a user made.
 *  - Numbers compare by value. `JSON.parse` already collapses PostgreSQL's
 *    `5000.00` and `5000` to the same JS number, so no extra normalisation is
 *    needed — but string values are *not* coerced, because a genuine
 *    string→number type change should still surface.
 */
export const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (isAbsent(a) && isAbsent(b)) return true;
  if (isAbsent(a) || isAbsent(b)) return false;
  if (a === b) return true;

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => valuesEqual(item, b[i]));
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((k) => valuesEqual(a[k], b[k]));
  }

  return false;
};

/**
 * True when a key appearing on only one side carries no signal — it was added
 * or dropped holding an empty default (`null`, `0`, `false`, `""`).
 *
 * This is what keeps a blob diff readable. A single observed `policy_data`
 * write gained ten keys as `0`/`null` and dropped six holding `false`/`0` while
 * only three values genuinely moved; without this rule the three real changes
 * are outnumbered five to one by writer-struct drift. A one-sided key holding a
 * *meaningful* value (say `is_lasered: true` being dropped) still reports.
 */
const isEmptyDefault = (v: unknown): boolean => v === null || v === 0 || v === false || v === "";

const isDriftOnly = (beforeMissing: boolean, afterMissing: boolean, before: unknown, after: unknown): boolean => {
  if (beforeMissing && !afterMissing) return isEmptyDefault(after);
  if (afterMissing && !beforeMissing) return isEmptyDefault(before);
  return false;
};

// ─── Labels ──────────────────────────────────────────────────────────────────

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ColumnMetadataEntry {
  label: string;
  tooltip: string;
}

/**
 * `allColumnsMetadata.json` is two-shaped: most top-level keys are a resource
 * name holding a map of columns, but the audit bookkeeping columns
 * (`created_by`, `deleted_datetime`, …) sit at the top level as bare entries.
 */
type ColumnMetadataNode = ColumnMetadataEntry | Record<string, ColumnMetadataEntry>;

const metadata = columnsMetadata as unknown as Record<string, ColumnMetadataNode | undefined>;

const isEntry = (node: ColumnMetadataNode | undefined): node is ColumnMetadataEntry =>
  typeof (node as ColumnMetadataEntry | undefined)?.label === "string";

const lookupLabel = (node: ColumnMetadataNode | undefined, key: string): string | undefined => {
  if (!node || isEntry(node)) return undefined;
  return node[key]?.label;
};

/** `total_paid_amount` → `Total Paid Amount`; trailing FK suffixes dropped. */
const humanise = (key: string): string =>
  key
    .replace(/_(id_ref|ref_id)$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/g, "ID")
    .replace(/\bUrl\b/g, "URL");

/**
 * Resolves a column's display label.
 *
 * Reads `allColumnsMetadata.json` directly rather than going through
 * `columnsHelper.getProps`, which `console.warn`s on every miss. A diff walks
 * whatever columns the trigger captured — including internal ones no screen
 * renders — so misses are expected and must stay silent.
 */
export const labelForColumn = (key: string, resource?: string): string => {
  const scoped = resource ? lookupLabel(metadata[resource], key) : undefined;
  if (scoped) return scoped;

  const shared = metadata[key];
  if (isEntry(shared)) return shared.label;

  return humanise(key);
};

/** Shortens a UUID path segment so a blob path stays readable. */
const shortenSegment = (segment: string): string => (UUID_REGEX.test(segment) ? `${segment.slice(0, 8)}…` : segment);

// ─── Blob walking ────────────────────────────────────────────────────────────

/**
 * Collects changed leaves from two blob values, recursing through nested
 * objects. Arrays are treated as leaves — an element-wise array diff reads
 * worse than "this list changed" for the shapes we store.
 */
const collectLeaves = (before: unknown, after: unknown, trail: string[], out: AuditLeafChange[]): void => {
  const atDepthLimit = trail.length >= MAX_LEAF_DEPTH;

  if (!atDepthLimit && (isPlainObject(before) || isPlainObject(after))) {
    const beforeObj = isPlainObject(before) ? before : {};
    const afterObj = isPlainObject(after) ? after : {};
    const keys = [...new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)])].sort();

    for (const key of keys) {
      const beforeMissing = !(key in beforeObj);
      const afterMissing = !(key in afterObj);
      if (isDriftOnly(beforeMissing, afterMissing, beforeObj[key], afterObj[key])) continue;
      collectLeaves(beforeObj[key], afterObj[key], [...trail, key], out);
    }
    return;
  }

  if (valuesEqual(before, after)) return;
  out.push({ path: trail.map(shortenSegment).join(" › "), before, after });
};

// ─── Diff ────────────────────────────────────────────────────────────────────

interface UpdateEnvelope {
  old: Record<string, unknown>;
  new: Record<string, unknown>;
}

/**
 * Narrows `json_data` to the UPDATE/SOFT_DELETE envelope. INSERT and DELETE
 * rows store the bare row, so `old`/`new` are absent and there is nothing to
 * diff against.
 */
const asUpdateEnvelope = (jsonData: unknown): UpdateEnvelope | null => {
  if (!isPlainObject(jsonData)) return null;
  const { old, new: next } = jsonData;
  if (!isPlainObject(old) || !isPlainObject(next)) return null;
  return { old, new: next };
};

/**
 * Computes the changed columns for one audit record.
 *
 * @param record   The `data_audit` row as returned by the API.
 * @param resource React Admin resource name, used to resolve field labels.
 * @returns Changed columns, or `null` when the record carries no old/new pair
 *          (INSERT, DELETE, or malformed `json_data`). An empty array is
 *          meaningful and distinct from `null`: the row was rewritten but no
 *          reportable column moved.
 */
export const diffAuditRecord = (record: DataAuditRecord, resource?: string): AuditFieldChange[] | null => {
  const envelope = asUpdateEnvelope(record.json_data);
  if (!envelope) return null;

  const { old: before, new: after } = envelope;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  const changes: AuditFieldChange[] = [];

  for (const key of keys) {
    if (NOISE_COLUMNS.has(key)) continue;

    const beforeValue = before[key];
    const afterValue = after[key];
    if (valuesEqual(beforeValue, afterValue)) continue;

    const isBlob = isPlainObject(beforeValue) || isPlainObject(afterValue);
    const change: AuditFieldChange = {
      key,
      label: labelForColumn(key, resource),
      kind: isBlob ? "blob" : "scalar",
      before: beforeValue,
      after: afterValue,
    };

    if (isBlob) {
      const leaves: AuditLeafChange[] = [];
      collectLeaves(beforeValue, afterValue, [], leaves);

      // Every leaf was drift-only, so nothing a reader would call a change.
      if (!leaves.length) continue;

      change.leaves = leaves.slice(0, MAX_LEAVES_PER_BLOB);
      change.hiddenLeafCount = Math.max(0, leaves.length - MAX_LEAVES_PER_BLOB);
    }

    changes.push(change);
  }

  return changes;
};

/**
 * Builds the one-line headline suffix, e.g. `Member ID, Notes and 2 more`.
 * Returns an empty string when there is nothing to name.
 */
export const summariseChanges = (changes: AuditFieldChange[], maxNamed = 3): string => {
  if (!changes.length) return "";

  const named = changes.slice(0, maxNamed).map((c) => c.label);
  const remainder = changes.length - named.length;

  if (!remainder) return named.join(", ");
  return `${named.join(", ")} and ${remainder} more`;
};
